import { useState, useEffect, useCallback, useMemo } from "react";

// ── SITE CONFIG (your team edits this JSON) ──
const DEFAULT_PAGES = {
  home: {
    slug: "home", title: "Tech Dynamic | Authorised Zoho Partner · Automation & Cloud Solutions Australia",
    metaDesc: "Tech Dynamic is an authorised Zoho Partner delivering business process automation, Microsoft 365, cloud solutions, CRM implementation, and custom software across Sydney, Canberra, and Melbourne.",
    h1: "Intelligent automation and cloud solutions for modern businesses.",
    keywords: "Zoho Partner Australia, business automation Sydney, CRM implementation, Microsoft 365 Australia, cloud solutions",
    content: "Tech Dynamic helps businesses simplify operations, integrate systems, deploy cloud solutions, and build scalable digital platforms using Zoho, Microsoft, AI, and custom software.",
    schema: "ProfessionalService", nav: true, navLabel: "Home"
  },
  services: {
    slug: "services", title: "Services | Tech Dynamic — Automation, Zoho, Microsoft 365, Custom Dev",
    metaDesc: "Business process automation, Zoho CRM implementation, Microsoft 365 deployment, custom software development, API integration, AI automation, and Excel macros for Australian businesses.",
    h1: "Solutions built around operations, efficiency, and growth.",
    keywords: "business process automation, Zoho implementation, Microsoft 365 setup, custom software development, API integration, AI automation",
    content: "We work with businesses that want clearer systems, stronger visibility, and less manual work across sales, operations, finance, and customer-facing processes.",
    services: [
      { icon: "⚙️", name: "Business Process Automation", desc: "Reduce manual work with intelligent workflows, approvals, document routing, and operational automation tailored to your business.", points: ["Workflow design & automation", "Approval routing & escalations", "Document automation", "Task scheduling & notifications"] },
      { icon: "🔷", name: "Zoho Implementation", desc: "Deploy Zoho CRM, Creator, Books, Desk, Flow, Projects, and WorkDrive with clean architecture and scalable automation.", points: ["Zoho CRM setup & customisation", "Zoho Creator app development", "Zoho Books & Finance", "Zoho One full-suite rollout"] },
      { icon: "☁️", name: "Microsoft & Cloud Solutions", desc: "Implement Microsoft 365, SharePoint, Teams, Entra ID, Azure, and secure collaboration environments for growing teams.", points: ["Microsoft 365 deployment", "SharePoint intranet setup", "Azure cloud configuration", "Teams & Entra ID governance"] },
      { icon: "💻", name: "Custom Software Development", desc: "Build modern web applications, client portals, internal systems, and industry-specific platforms that fit your process.", points: ["Web application development", "Client & staff portals", "Internal operational systems", "Industry-specific platforms"] },
      { icon: "🔗", name: "System Integration & APIs", desc: "Connect CRM, ERP, finance, logistics, and support systems using reliable integrations and real-time data flows.", points: ["REST & webhook integrations", "CRM ↔ ERP data sync", "Finance & logistics connectors", "Real-time data pipelines"] },
      { icon: "🤖", name: "AI & Document Intelligence", desc: "Use AI to extract invoice data, automate classification, improve decision-making, and accelerate repetitive tasks.", points: ["AI invoice data extraction", "Automated document classification", "AI-assisted decision workflows", "Intelligent task acceleration"] },
      { icon: "📊", name: "Excel Macros & Spreadsheet Automation", desc: "Eliminate repetitive manual work in Excel with custom VBA Macros, automated reporting, and seamless integration.", points: ["Custom VBA Macro development", "Automated data extraction", "Dynamic Excel dashboards", "Excel ↔ Zoho integration"] }
    ],
    schema: "Service", nav: true, navLabel: "Services"
  },
  zoho: {
    slug: "zoho", title: "Zoho Partner Australia | Zoho CRM, Creator, One Implementation — Tech Dynamic",
    metaDesc: "Authorised Zoho Partner in Australia. We implement Zoho CRM, Creator, Books, Desk, Flow, Analytics, and Zoho One for businesses in Sydney, Canberra, and Melbourne.",
    h1: "Zoho Solutions, Delivered Right.",
    keywords: "Zoho Partner Australia, Zoho CRM Sydney, Zoho implementation, Zoho One, Zoho Creator, authorised Zoho partner",
    content: "As an authorised Zoho Partner, Tech Dynamic designs, configures, and deploys Zoho solutions tailored to how Australian businesses actually operate — from CRM to full Zoho One suite rollouts.",
    products: [
      { abbr: "CRM", name: "Zoho CRM", desc: "Pipeline & sales automation" }, { abbr: "BGN", name: "Zoho Bigin", desc: "Lightweight CRM" },
      { abbr: "CRT", name: "Zoho Creator", desc: "Low-code apps" }, { abbr: "FLW", name: "Zoho Flow", desc: "900+ app automation" },
      { abbr: "BKS", name: "Zoho Books", desc: "Accounting & GST" }, { abbr: "INV", name: "Zoho Inventory", desc: "Stock control" },
      { abbr: "DSK", name: "Zoho Desk", desc: "Helpdesk & SLA" }, { abbr: "ANL", name: "Zoho Analytics", desc: "BI dashboards" },
      { abbr: "PEP", name: "Zoho People", desc: "HR management" }, { abbr: "PRJ", name: "Zoho Projects", desc: "Task management" },
      { abbr: "SIQ", name: "Zoho SalesIQ", desc: "Live chat & AI" }, { abbr: "ONE", name: "Zoho One", desc: "Full business OS" }
    ],
    schema: "Service", nav: true, navLabel: "Zoho"
  },
  industries: {
    slug: "industries", title: "Industries We Serve | Construction, Healthcare, Finance — Tech Dynamic",
    metaDesc: "We design systems for construction, manufacturing, logistics, professional services, healthcare, retail, finance, and property businesses across Australia.",
    h1: "Built for businesses across multiple sectors.",
    keywords: "Zoho for construction, CRM for healthcare, automation for logistics, business software manufacturing",
    content: "We design systems that reflect how each industry actually operates — not generic templates.",
    industries: [
      { icon: "🏗️", name: "Construction", desc: "Project tracking, subcontractor management, site documentation workflows, and compliance automation." },
      { icon: "🏭", name: "Manufacturing", desc: "Production scheduling, inventory control, supplier integration, and quality management systems." },
      { icon: "🚚", name: "Logistics", desc: "Fleet tracking, dispatch automation, customer updates, and ERP connectivity for freight operations." },
      { icon: "💼", name: "Professional Services", desc: "CRM pipelines, client onboarding automation, billing integration, and knowledge management." },
      { icon: "🏥", name: "Healthcare", desc: "Patient records management, appointment workflows, compliance tracking, and secure data handling." },
      { icon: "🛒", name: "Retail", desc: "Inventory automation, POS integrations, customer loyalty systems, and e-commerce data flows." },
      { icon: "💰", name: "Finance", desc: "Invoice automation, compliance reporting, client onboarding workflows, and document management." },
      { icon: "🏢", name: "Property & Real Estate", desc: "Lead tracking, tenancy management, maintenance workflows, and landlord reporting automation." }
    ],
    schema: "Service", nav: true, navLabel: "Industries"
  },
  results: {
    slug: "results", title: "Client Results & Case Studies | Tech Dynamic Australia",
    metaDesc: "Real outcomes from real projects. Invoice automation, CRM transformation, cloud migration, and workflow automation case studies from Tech Dynamic.",
    h1: "Trusted by businesses across Australia.",
    keywords: "Zoho case study, CRM implementation results, business automation case study, invoice automation",
    content: "Real outcomes from real projects. Names withheld — we let the results speak.",
    cases: [
      { industry: "Logistics · Sydney", metric: "60%", label: "Reduction in invoice processing time", desc: "Automated email ingestion, AI data extraction, and SAP sync eliminated manual data entry across their finance team.", tech: "Zoho Creator · AI Automation · SAP Integration" },
      { industry: "Professional Services · Canberra", metric: "3×", label: "Faster client onboarding process", desc: "CRM pipeline redesign, automated task creation, and document generation cut onboarding from 3 weeks to under 7 days.", tech: "Zoho CRM · Zoho Sign · WorkDrive" },
      { industry: "Construction · Melbourne", metric: "100%", label: "Elimination of paper-based site forms", desc: "Custom Zoho Creator app replaced all paper forms across 12 active sites with real-time reporting to head office.", tech: "Zoho Creator · Zoho Analytics" },
      { industry: "Healthcare · Sydney", metric: "8hrs", label: "Saved per week in admin tasks", desc: "Appointment scheduling, compliance tracking, and patient communication workflows automated across a 4-clinic group.", tech: "Zoho CRM · Zoho Desk · Power Automate" },
      { industry: "Retail · Melbourne", metric: "4 sys", label: "Disconnected systems unified into one", desc: "POS, inventory, e-commerce, and accounting platforms connected via custom API integration with live stock sync.", tech: "Zoho Inventory · Xero · Shopify · REST APIs" },
      { industry: "Finance · Canberra", metric: "40%", label: "Faster month-end reporting cycle", desc: "Excel macro automation consolidated data from 6 source systems into a single report — previously a 2-day manual process.", tech: "Excel VBA · Zoho Analytics · SharePoint" }
    ],
    schema: "Article", nav: true, navLabel: "Results"
  },
  support: {
    slug: "support", title: "Support Packages | Zoho & Cloud Support Plans — Tech Dynamic",
    metaDesc: "Ongoing support packages for Zoho, Microsoft 365, and custom systems. Essentials, Business, Growth, and Enterprise tiers with Australian-based team.",
    h1: "Support packages to keep your systems running & growing.",
    keywords: "Zoho support Australia, managed Zoho services, IT support packages, Zoho maintenance",
    content: "After go-live, your systems need care. Our support packages give you a dedicated team to handle issues, enhancements, and strategic growth — without hiring in-house.",
    tiers: [
      { name: "Essentials", sub: "Get Started", desc: "For small teams with basic support needs", features: ["Up to 5 hrs support/month", "Email-based support", "2 business day response", "Bug fixes & user management"], missing: ["Phone support", "Quarterly reviews"] },
      { name: "Business", sub: "Most Popular", desc: "For growing teams that need responsive support", features: ["Up to 10 hrs support/month", "Email & phone support", "Same business day response", "Bug fixes & minor enhancements", "Monthly check-in call"], missing: ["Quarterly strategy review"] },
      { name: "Growth", sub: "Scale Faster", desc: "For businesses actively evolving their systems", recommended: true, features: ["Up to 20 hrs support/month", "Priority email & phone", "4-hour response SLA", "Enhancements & new features", "Monthly check-in call", "Quarterly strategy review"], missing: [] },
      { name: "Enterprise", sub: "Custom", desc: "For large teams with complex ongoing requirements", features: ["Dedicated support resource", "Custom SLA agreement", "On-site support available", "Proactive system monitoring", "Dedicated account manager", "Monthly strategy sessions"], missing: [] }
    ],
    schema: "Service", nav: true, navLabel: "Support"
  },
  "locations-sydney": {
    slug: "locations-sydney", title: "Zoho Partner Sydney | CRM & Automation — Tech Dynamic NSW",
    metaDesc: "Sydney-based Zoho Partner delivering CRM implementation, business automation, Microsoft 365, and custom software. Level 1/5 George St, North Strathfield NSW.",
    h1: "Zoho Partner & IT Solutions in Sydney.",
    keywords: "Zoho Partner Sydney, CRM implementation Sydney, business automation NSW, IT solutions North Strathfield",
    content: "Tech Dynamic's Sydney office delivers Zoho CRM, business automation, Microsoft 365, and custom software to businesses across NSW. Local team, local support.",
    address: "Level 1/5 George St, North Strathfield NSW 2137", schema: "LocalBusiness", nav: false
  },
  "locations-canberra": {
    slug: "locations-canberra", title: "Zoho Partner Canberra | CRM & Cloud Solutions — Tech Dynamic ACT",
    metaDesc: "Canberra-based Zoho Partner for government, professional services, and growing businesses in the ACT region.",
    h1: "Zoho Partner & IT Solutions in Canberra.",
    keywords: "Zoho Partner Canberra, CRM implementation ACT, business automation Canberra, IT solutions Phillip",
    content: "Tech Dynamic's Canberra office supports government, professional services, and growing businesses across the ACT with Zoho, Microsoft 365, and custom solutions.",
    address: "Level 2/7 Neptune St, Phillip ACT 2606", schema: "LocalBusiness", nav: false
  },
  "locations-melbourne": {
    slug: "locations-melbourne", title: "Zoho Partner Melbourne | CRM & Automation — Tech Dynamic VIC",
    metaDesc: "Melbourne Zoho Partner delivering CRM, automation, Microsoft 365, and custom software to Victorian businesses. Collins St, Melbourne.",
    h1: "Zoho Partner & IT Solutions in Melbourne.",
    keywords: "Zoho Partner Melbourne, CRM implementation VIC, business automation Melbourne, IT solutions Collins St",
    content: "Tech Dynamic's Melbourne office delivers end-to-end Zoho implementation, cloud migration, and business automation for Victorian businesses.",
    address: "Level 21/567 Collins St, Melbourne VIC 3000", schema: "LocalBusiness", nav: false
  },
  contact: {
    slug: "contact", title: "Contact Tech Dynamic | Book a Free Consultation — Sydney, Canberra, Melbourne",
    metaDesc: "Contact Tech Dynamic for Zoho implementation, Microsoft 365, business automation, or custom software. Free 30-minute discovery call. Australian-based team.",
    h1: "Let's discuss your next project.",
    keywords: "contact Tech Dynamic, Zoho consultation Australia, book IT consultation Sydney",
    content: "Whether you need CRM implementation, Microsoft 365 setup, AI automation, or custom software — Tech Dynamic can help you plan and deliver the right solution.",
    schema: "ContactPage", nav: true, navLabel: "Contact"
  }
};

// ── SEO SCORING ENGINE ──
function calcSEO(page) {
  let score = 0; const issues = []; const wins = [];
  // Title
  if (page.title) {
    const tl = page.title.length;
    if (tl >= 30 && tl <= 60) { score += 15; wins.push("Title length is ideal (" + tl + " chars)"); }
    else if (tl > 0 && tl < 30) { score += 5; issues.push("Title too short (" + tl + "/30-60 chars)"); }
    else if (tl > 60) { score += 8; issues.push("Title too long (" + tl + "/60 max chars) — may get truncated"); }
  } else { issues.push("Missing page title"); }
  // Meta desc
  if (page.metaDesc) {
    const ml = page.metaDesc.length;
    if (ml >= 120 && ml <= 160) { score += 15; wins.push("Meta description length ideal (" + ml + ")"); }
    else if (ml > 0 && ml < 120) { score += 5; issues.push("Meta description too short (" + ml + "/120-160)"); }
    else if (ml > 160) { score += 8; issues.push("Meta description too long (" + ml + "/160 max) — will truncate"); }
  } else { issues.push("Missing meta description"); }
  // H1
  if (page.h1 && page.h1.length > 5) { score += 12; wins.push("H1 heading present"); } else { issues.push("Missing or too short H1 heading"); }
  // Keywords
  if (page.keywords) {
    const kw = page.keywords.split(",").map(k => k.trim()).filter(Boolean);
    if (kw.length >= 3 && kw.length <= 8) { score += 10; wins.push(kw.length + " keywords targeted"); }
    else if (kw.length < 3) { score += 4; issues.push("Too few keywords (" + kw.length + "/3-8)"); }
    else { score += 6; issues.push("Too many keywords (" + kw.length + ") — focus on 3-8"); }
    // Check if primary keyword in title
    if (kw[0] && page.title && page.title.toLowerCase().includes(kw[0].toLowerCase().split(" ")[0])) {
      score += 8; wins.push("Primary keyword appears in title");
    } else { issues.push("Primary keyword not found in title"); }
    // Check keyword in meta desc
    if (kw[0] && page.metaDesc && page.metaDesc.toLowerCase().includes(kw[0].toLowerCase().split(" ")[0])) {
      score += 5; wins.push("Primary keyword in meta description");
    } else { issues.push("Primary keyword missing from meta description"); }
  } else { issues.push("No keywords defined"); }
  // Content length
  if (page.content) {
    const wc = page.content.split(/\s+/).length;
    if (wc >= 50) { score += 10; wins.push("Content has " + wc + " words"); }
    else { score += 3; issues.push("Content too thin (" + wc + " words) — aim for 300+"); }
  } else { issues.push("No content body"); }
  // Schema
  if (page.schema) { score += 8; wins.push("Schema type set: " + page.schema); } else { issues.push("No schema markup configured"); }
  // Slug
  if (page.slug && !page.slug.includes(" ") && page.slug === page.slug.toLowerCase()) {
    score += 7; wins.push("Clean URL slug: /" + page.slug);
  } else { issues.push("Slug should be lowercase with hyphens"); }
  // Internal links check
  score += 5; wins.push("Internal navigation linked");
  // Cap at 100
  score = Math.min(100, Math.max(0, score));
  return { score, issues, wins };
}

// ── COLOR HELPERS ──
function scoreColor(s) {
  if (s >= 80) return "#22c55e";
  if (s >= 60) return "#f59e0b";
  return "#ef4444";
}

// ── GLASS CARD ──
const Glass = ({ children, style, className = "" }) => (
  <div className={className} style={{
    background: "rgba(0,0,0,.48)", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
    borderRadius: 20, border: "1px solid rgba(255,255,255,.1)", position: "relative", overflow: "hidden", ...style
  }}>{children}</div>
);

const GlassLight = ({ children, style }) => (
  <div style={{
    background: "rgba(255,255,255,.06)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
    borderRadius: 14, border: "1px solid rgba(255,255,255,.08)", overflow: "hidden", ...style
  }}>{children}</div>
);

// ── NAV ──
function Nav({ pages, current, onNav, onAdmin, isAdmin }) {
  const navPages = Object.values(pages).filter(p => p.nav);
  return (
    <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, padding: "14px 40px" }}>
      <Glass style={{ maxWidth: 1280, margin: "0 auto", height: 64, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px" }}>
        <div onClick={() => onNav("home")} style={{ cursor: "pointer", display: "flex", flexDirection: "column", gap: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width={24} height={24} viewBox="0 0 26 26" fill="none"><rect x={0} y={3} width={9} height={20} rx={2.5} fill="#fff"/><rect x={13} y={3} width={9} height={20} rx={2.5} fill="#fff" opacity={.35}/></svg>
            <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-.03em", color: "#fff" }}>Tech<span style={{ fontWeight: 200, color: "rgba(255,255,255,.6)" }}> Dynamic</span></span>
          </div>
          <span style={{ fontSize: 8, fontWeight: 600, letterSpacing: ".16em", textTransform: "uppercase", color: "rgba(255,255,255,.3)", paddingLeft: 34 }}>Automation · Cloud · Integrations</span>
        </div>
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          {navPages.map(p => (
            <span key={p.slug} onClick={() => onNav(p.slug)}
              style={{ fontSize: 13, fontWeight: 500, color: current === p.slug ? "#fff" : "rgba(255,255,255,.45)", cursor: "pointer", transition: "color .2s" }}>
              {p.navLabel}
            </span>
          ))}
          <span onClick={onAdmin} style={{
            fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: isAdmin ? "#22c55e" : "rgba(255,255,255,.4)",
            cursor: "pointer", padding: "5px 12px", borderRadius: 999,
            background: isAdmin ? "rgba(34,197,94,.12)" : "rgba(255,255,255,.06)", border: "1px solid " + (isAdmin ? "rgba(34,197,94,.3)" : "rgba(255,255,255,.08)")
          }}>ADMIN</span>
        </div>
      </Glass>
    </nav>
  );
}

// ── REUSABLE COMPONENTS ──
const Divider = () => <div style={{ height: 1, background: "rgba(255,255,255,.06)", margin: "64px 0" }}/>;
const CTA = ({ onNav, text = "Book a Free Discovery Call", sub = "No obligation · No lock-in · Australian team" }) => (
  <div style={{ textAlign: "center", padding: "64px 0 32px" }}>
    <button onClick={() => onNav("contact")} style={{ background: "#fff", color: "#000", padding: "14px 32px", borderRadius: 999, fontSize: 14, fontWeight: 700, border: "none", cursor: "pointer" }}>{text} ↗</button>
    <div style={{ fontSize: 12, color: "rgba(255,255,255,.3)", marginTop: 12 }}>{sub}</div>
  </div>
);
const FeatureRow = ({ items }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(items.length, 3)},1fr)`, gap: 16, marginTop: 32 }}>
    {items.map((it, i) => (
      <GlassLight key={i} style={{ padding: 24, borderRadius: 16 }}>
        {it.icon && <span style={{ fontSize: 22, display: "block", marginBottom: 12 }}>{it.icon}</span>}
        <h4 style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 6 }}>{it.title}</h4>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,.42)", lineHeight: 1.65 }}>{it.desc}</p>
      </GlassLight>
    ))}
  </div>
);
const ProcessSteps = ({ steps }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${steps.length},1fr)`, gap: 0, marginTop: 40, position: "relative" }}>
    <div style={{ position: "absolute", top: 24, left: 24, right: 24, height: 1, background: "rgba(255,255,255,.07)" }}/>
    {steps.map((s, i) => (
      <div key={i} style={{ padding: "0 16px", position: "relative", zIndex: 1 }}>
        <Glass style={{ width: 48, height: 48, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{i + 1}</Glass>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 6 }}>{s.title}</h4>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,.42)", lineHeight: 1.65 }}>{s.desc}</p>
      </div>
    ))}
  </div>
);
const FAQSection = ({ items }) => {
  const [openIdx, setOpenIdx] = useState(-1);
  return (
    <div style={{ marginTop: 40 }}>
      {items.map((faq, i) => (
        <div key={i} onClick={() => setOpenIdx(openIdx === i ? -1 : i)} style={{ borderBottom: "1px solid rgba(255,255,255,.06)", cursor: "pointer", padding: "18px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{faq.q}</span>
            <span style={{ fontSize: 18, color: "rgba(255,255,255,.3)", transform: openIdx === i ? "rotate(45deg)" : "none", transition: "transform .2s" }}>+</span>
          </div>
          {openIdx === i && <p style={{ fontSize: 13, color: "rgba(255,255,255,.45)", lineHeight: 1.7, marginTop: 10, paddingRight: 32 }}>{faq.a}</p>}
        </div>
      ))}
    </div>
  );
};

// ── PAGE RENDERERS ──
function HomePage({ page, onNav }) {
  return (
    <div>
      {/* Hero */}
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "160px 64px 80px", maxWidth: 1280, margin: "0 auto", position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".18em", textTransform: "uppercase", color: "rgba(255,255,255,.4)", marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 20, height: 1, background: "rgba(255,255,255,.3)", display: "inline-block" }}/>Digital Transformation Partner
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 48, alignItems: "end" }}>
          <div>
            <h1 style={{ fontSize: "clamp(44px,6vw,82px)", fontWeight: 300, letterSpacing: "-.04em", lineHeight: 1.05 }}>
              <strong style={{ fontWeight: 800 }}>Intelligent automation</strong><br/>
              <span style={{ color: "rgba(255,255,255,.5)" }}>and cloud solutions for modern businesses.</span>
            </h1>
            <p style={{ fontSize: 16, color: "rgba(255,255,255,.5)", lineHeight: 1.7, maxWidth: 520, margin: "22px 0 32px" }}>{page.content}</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button onClick={() => onNav("contact")} style={{ background: "#fff", color: "#000", padding: "13px 28px", borderRadius: 999, fontSize: 14, fontWeight: 700, border: "none", cursor: "pointer" }}>Start Your Project ↗</button>
              <button onClick={() => onNav("services")} style={{ padding: "13px 24px", borderRadius: 999, fontSize: 14, fontWeight: 600, color: "#fff", border: "1.5px solid rgba(255,255,255,.2)", background: "transparent", cursor: "pointer" }}>Explore Services</button>
            </div>
          </div>
          <Glass style={{ padding: 24, borderRadius: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: "rgba(255,255,255,.6)", marginBottom: 16, width: "fit-content" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px rgba(34,197,94,.7)" }}/>Authorised Zoho Partner
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: 16, marginBottom: 16, borderBottom: "1px solid rgba(255,255,255,.08)" }}>
              <div><div style={{ fontSize: 30, fontWeight: 200, color: "#fff" }}>50+</div><div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.35)", marginTop: 3 }}>Projects Delivered</div></div>
              <div style={{ textAlign: "right" }}><div style={{ fontSize: 30, fontWeight: 200, color: "#fff" }}>5★</div><div style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.35)", marginTop: 3 }}>Client Rating</div></div>
            </div>
            {["Zoho CRM & One Suite", "Business Automation", "Microsoft 365 & Azure", "Custom Software", "API Integrations"].map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 500, letterSpacing: ".06em", textTransform: "uppercase", color: i === 0 ? "#fff" : "rgba(255,255,255,.25)", padding: "5px 0" }}>
                <span style={{ width: 4, height: 4, borderRadius: "50%", background: i === 0 ? "#fff" : "rgba(255,255,255,.15)" }}/>{s}
              </div>
            ))}
          </Glass>
        </div>
      </div>

      {/* Stats */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,.06)", borderBottom: "1px solid rgba(255,255,255,.06)", padding: "44px 64px", background: "rgba(0,0,0,.3)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)" }}>
          {[["50+", "Zoho Projects Delivered"], ["10+", "Zoho Products Implemented"], ["100%", "Australian-Based Support"], ["5★", "Average Client Rating"]].map(([v, l], i) => (
            <div key={i} style={{ padding: "0 36px", borderRight: i < 3 ? "1px solid rgba(255,255,255,.06)" : "none" }}>
              <div style={{ fontSize: 44, fontWeight: 200, letterSpacing: "-.04em", color: "#fff", lineHeight: 1, marginBottom: 6 }}>{v}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)" }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* What We Do summary */}
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 64px" }}>
        <SectionHeader label="What We Do" h2="End-to-end digital delivery for Australian businesses." sub="From requirement discovery to go-live support, we implement systems with confidence — not guesswork."/>
        <FeatureRow items={[
          { icon: "⚙️", title: "Automation", desc: "Workflow-led process improvement. Approvals, document routing, scheduling, and task automation across your operations." },
          { icon: "🔗", title: "Integrations", desc: "Reliable data flow across systems. REST APIs, webhook connectors, CRM-to-ERP sync, and real-time data pipelines." },
          { icon: "☁️", title: "Cloud", desc: "Secure Microsoft 365 & Azure deployment. SharePoint intranets, Teams governance, identity management, and cloud migration." }
        ]}/>
        <Divider/>

        {/* Featured capabilities */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          <Glass style={{ padding: 32, borderRadius: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.35)", marginBottom: 14 }}>Featured Capability</div>
            <h3 style={{ fontSize: 20, fontWeight: 600, color: "#fff", marginBottom: 10 }}>Invoice Automation</h3>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,.45)", lineHeight: 1.7, marginBottom: 16 }}>Email capture, AI extraction, validation workflows, approval routing, and system sync for finance operations. Reduce manual data entry by up to 60%.</p>
            <button onClick={() => onNav("results")} style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: "none", border: "1px solid rgba(255,255,255,.15)", borderRadius: 999, padding: "7px 16px", cursor: "pointer" }}>See Case Study →</button>
          </Glass>
          <Glass style={{ padding: 32, borderRadius: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.35)", marginBottom: 14 }}>Implementation Focus</div>
            <h3 style={{ fontSize: 20, fontWeight: 600, color: "#fff", marginBottom: 10 }}>Zoho + Microsoft</h3>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,.45)", lineHeight: 1.7, marginBottom: 16 }}>CRM, Creator, WorkDrive, SharePoint, Azure, Teams, and process automation aligned to business workflows. 50+ projects delivered.</p>
            <button onClick={() => onNav("zoho")} style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: "none", border: "1px solid rgba(255,255,255,.15)", borderRadius: 999, padding: "7px 16px", cursor: "pointer" }}>Explore Zoho →</button>
          </Glass>
        </div>
        <Divider/>

        {/* Why Clients */}
        <SectionHeader label="Why Clients Engage Us" h2="Practical systems that teams can actually use."/>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, marginTop: 32 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: "#fff", marginBottom: 16 }}>End-to-End Delivery</h3>
            {["Business analysis and solution design", "Implementation, configuration, and integration", "Support, optimisation, and scaling", "Role-specific training and documentation", "Quarterly reviews and ongoing enhancements"].map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: "rgba(255,255,255,.5)", padding: "6px 0" }}>
                <span style={{ color: "#22c55e", fontWeight: 700 }}>✓</span>{t}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignContent: "flex-start" }}>
            {["Zoho CRM", "Zoho Creator", "Microsoft 365", "Azure", "Power Automate", "SharePoint", "Excel Macros", "API Integrations", "AI Automation", "Zoho One"].map((t, i) => (
              <span key={i} style={{ padding: "6px 14px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,.5)", border: "1px solid rgba(255,255,255,.1)", background: "rgba(255,255,255,.03)" }}>{t}</span>
            ))}
          </div>
        </div>
        <Divider/>

        {/* Quick links */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
          {[
            { icon: "⚙️", label: "Services", desc: "Automation, Zoho, Microsoft, custom dev & more", to: "services" },
            { icon: "🔗", label: "Solutions", desc: "Platforms and tech stacks we specialise in", to: "solutions" },
            { icon: "🏭", label: "Industries", desc: "Sectors we've built systems for", to: "industries" },
            { icon: "📋", label: "Case Studies", desc: "Real transformation examples", to: "results" }
          ].map((l, i) => (
            <Glass key={i} style={{ padding: 24, borderRadius: 16, cursor: "pointer" }} onClick={() => onNav(l.to)}>
              <span style={{ fontSize: 20, display: "block", marginBottom: 10 }}>{l.icon}</span>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 4 }}>{l.label}</h4>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,.4)" }}>{l.desc}</p>
            </Glass>
          ))}
        </div>

        <CTA onNav={onNav}/>
      </div>
    </div>
  );
}

function ServicesPage({ page, onNav }) {
  return (
    <div style={{ padding: "160px 64px 80px", maxWidth: 1280, margin: "0 auto" }}>
      <SectionHeader label="What We Do" h2={page.h1} sub={page.content}/>

      {/* Service cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18, marginTop: 48 }}>
        {(page.services || []).map((s, i) => (
          <Glass key={i} style={{ padding: 32, borderRadius: 20 }}>
            <span style={{ fontSize: 24, display: "block", marginBottom: 18 }}>{s.icon}</span>
            <h3 style={{ fontSize: 17, fontWeight: 600, color: "#fff", marginBottom: 10, letterSpacing: "-.01em" }}>{s.name}</h3>
            <p style={{ fontSize: 13, lineHeight: 1.7, color: "rgba(255,255,255,.45)", marginBottom: 14 }}>{s.desc}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {(s.points || []).map((pt, j) => (
                <span key={j} style={{ fontSize: 12, color: "rgba(255,255,255,.4)", display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "rgba(255,255,255,.25)", fontSize: 11 }}>→</span>{pt}
                </span>
              ))}
            </div>
          </Glass>
        ))}
      </div>

      <Divider/>

      {/* Delivery approach */}
      <SectionHeader label="Our Approach" h2="How we deliver every project."/>
      <ProcessSteps steps={[
        { title: "Discovery", desc: "Understand your process, data flows, current tools, and pain points before recommending anything." },
        { title: "Solution Design", desc: "Architecture, data model, and integration map scoped clearly. Approved by you before build begins." },
        { title: "Build & Configure", desc: "Iterative delivery with regular check-ins, staged testing, and feedback loops at every milestone." },
        { title: "Go-Live & Support", desc: "Role-specific training, supported go-live with us on-call week one, and ongoing quarterly reviews." }
      ]}/>

      <Divider/>

      {/* Why us for services */}
      <SectionHeader label="Why Tech Dynamic" h2="A practical partner for implementation and scale."/>
      <FeatureRow items={[
        { icon: "🎯", title: "Business-first approach", desc: "We focus on process clarity, usability, and outcomes rather than just tools and configuration. Every recommendation ties to a business goal." },
        { icon: "🔄", title: "End-to-end delivery", desc: "From requirement discovery to go-live support, we help clients implement solutions with confidence. One team, one point of contact." },
        { icon: "🇦🇺", title: "100% Australian team", desc: "No offshore handoffs. Every project is scoped, built, and supported by our local team across Sydney, Canberra, and Melbourne." }
      ]}/>

      <Divider/>

      {/* FAQ */}
      <SectionHeader label="Common Questions" h2="Services FAQ"/>
      <FAQSection items={[
        { q: "How long does a typical project take?", a: "Most Zoho implementations take 4-8 weeks. Microsoft 365 deployments are usually 2-4 weeks. Custom software varies based on scope but typically 6-12 weeks. We provide a clear timeline before any work begins." },
        { q: "Do you offer ongoing support after go-live?", a: "Yes. We offer four support tiers from Essentials to Enterprise. Every client gets a supported go-live with us on-call for the first week, followed by your chosen support plan." },
        { q: "Can you work with our existing systems?", a: "Absolutely. Most of our projects involve connecting to existing platforms — SAP, MYOB, Xero, Shopify, and dozens of others via REST APIs and custom integrations." },
        { q: "What if we're not sure which service we need?", a: "Book a free 30-minute discovery call. We'll listen to your challenges and recommend the right approach — no obligation, no pitch." },
        { q: "Do you work with small businesses?", a: "Yes. We work with businesses from 5 to 500+ people. The approach scales — a CRM for 5 users uses the same methodology as a Zoho One rollout for 200." }
      ]}/>

      <CTA onNav={onNav} text="Not sure which service fits?" sub="Book a free 30-minute discovery call — we'll help you identify the right approach."/>
    </div>
  );
}

function ZohoPage({ page, onNav }) {
  return (
    <div style={{ padding: "160px 64px 80px", maxWidth: 1280, margin: "0 auto" }}>
      <SectionHeader label="Authorised Zoho Partner" h2={page.h1} sub={page.content}/>

      <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12, padding: "12px 16px", width: "fit-content", marginTop: 24, marginBottom: 40 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 8px rgba(34,197,94,.6)" }}/>
        <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,.6)", letterSpacing: ".04em" }}>Certified & Authorised · Zoho Partner Australia</span>
      </div>

      {/* Stats bar */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0, padding: "28px 0", borderTop: "1px solid rgba(255,255,255,.06)", borderBottom: "1px solid rgba(255,255,255,.06)", marginBottom: 40 }}>
        {[["50+", "Zoho Projects"], ["16", "Products We Implement"], ["100%", "Australian Support"], ["5★", "Client Rating"]].map(([v, l], i) => (
          <div key={i} style={{ padding: "0 28px", borderRight: i < 3 ? "1px solid rgba(255,255,255,.06)" : "none" }}>
            <div style={{ fontSize: 32, fontWeight: 200, color: "#fff", lineHeight: 1 }}>{v}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.35)", marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Product grid */}
      <SectionHeader label="Zoho Products" h2="Products we implement and support."/>
      <p style={{ fontSize: 14, color: "rgba(255,255,255,.45)", lineHeight: 1.7, maxWidth: 600, marginBottom: 32 }}>We're across the full Zoho suite. Whether you need a single product deployed cleanly or the entire Zoho One ecosystem connected end-to-end, we've done it.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
        {(page.products || []).map((p, i) => (
          <GlassLight key={i} style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, borderRadius: 14 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, letterSpacing: ".04em", color: "rgba(255,255,255,.5)", flexShrink: 0 }}>{p.abbr}</div>
            <div><div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{p.name}</div><div style={{ fontSize: 11, color: "rgba(255,255,255,.35)" }}>{p.desc}</div></div>
          </GlassLight>
        ))}
      </div>

      <Divider/>

      {/* Why TD for Zoho */}
      <SectionHeader label="Why Tech Dynamic" h2="We don't just install Zoho — we make it work for your business."/>
      <FeatureRow items={[
        { icon: "🏅", title: "Authorised Partner Status", desc: "Certified Zoho partner with direct support escalation paths and partner-only resources from Zoho's engineering team." },
        { icon: "🔧", title: "Deep Technical Configuration", desc: "Custom modules, Deluge scripting, blueprint automation, and complex multi-app workflows built precisely for your process." },
        { icon: "🔗", title: "Cross-Platform Integration", desc: "Connect Zoho to Microsoft 365, SAP, Xero, MYOB, Shopify, and custom internal systems so your data stays in sync." }
      ]}/>
      <FeatureRow items={[
        { icon: "🇦🇺", title: "Australian-Based Support", desc: "Our team is in Sydney, Canberra, and Melbourne. Support in your timezone, understanding local compliance including GST and payroll." },
        { icon: "🎓", title: "Training & Adoption", desc: "We train your team role-by-role — admins, sales reps, managers — so people actually use the system and you get real ROI." },
        { icon: "📈", title: "Ongoing Optimisation", desc: "As your business evolves, your Zoho setup should too. We provide ongoing support, new feature rollouts, and quarterly reviews." }
      ]}/>

      <Divider/>

      {/* Implementation process */}
      <SectionHeader label="How We Implement" h2="Our Zoho implementation process."/>
      <ProcessSteps steps={[
        { title: "Discovery", desc: "Understand your workflows, data, current tools, and pain points before any configuration begins." },
        { title: "Solution Design", desc: "Map modules, fields, automation rules, and integrations — approved by you before we build anything." },
        { title: "Build & Configure", desc: "Configure, customise, and connect your Zoho environment with staged testing throughout." },
        { title: "Go-Live & Evolve", desc: "Training, supported go-live, then ongoing support and quarterly reviews to keep growing." }
      ]}/>

      <Divider/>

      {/* FAQ */}
      <SectionHeader label="Zoho FAQ" h2="Common questions about Zoho implementation."/>
      <FAQSection items={[
        { q: "How long does a Zoho CRM implementation take?", a: "A standard Zoho CRM setup takes 3-6 weeks depending on complexity. Full Zoho One deployments typically take 8-12 weeks. We provide a detailed timeline during the scoping phase." },
        { q: "Can you migrate our data from another CRM?", a: "Yes. We handle data migration from Salesforce, HubSpot, Pipedrive, spreadsheets, and legacy systems. We map fields, clean data, and validate before import." },
        { q: "Do we need Zoho One or just Zoho CRM?", a: "It depends on your needs. If you only need sales pipeline management, standalone CRM is fine. If you need finance, HR, project management, and helpdesk all connected, Zoho One gives you 45+ apps under one license." },
        { q: "What happens if something breaks after go-live?", a: "Our support packages cover that. Essentials gives you email support within 2 days. Growth gives you priority 4-hour SLA. We're always reachable." },
        { q: "Can Zoho integrate with our accounting software?", a: "Yes. Zoho Books handles accounting natively, or we can integrate with Xero, MYOB, or QuickBooks via Zoho Flow or custom API connections." }
      ]}/>

      <CTA onNav={onNav} text="Ready to get started with Zoho?" sub="Free scoping session · No lock-in · Certified Zoho experts"/>
    </div>
  );
}

function IndustriesPage({ page, onNav }) {
  return (
    <div style={{ padding: "160px 64px 80px", maxWidth: 1280, margin: "0 auto" }}>
      <SectionHeader label="Industries" h2={page.h1} sub={page.content}/>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginTop: 48 }}>
        {(page.industries || []).map((ind, i) => (
          <Glass key={i} style={{ padding: 28, borderRadius: 18 }}>
            <span style={{ fontSize: 22, display: "block", marginBottom: 14 }}>{ind.icon}</span>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: "#fff", marginBottom: 8 }}>{ind.name}</h3>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,.42)", lineHeight: 1.65 }}>{ind.desc}</p>
          </Glass>
        ))}
      </div>

      <Divider/>

      {/* Common challenges */}
      <SectionHeader label="Problems We Solve" h2="Common business challenges we address."/>
      <FeatureRow items={[
        { icon: "📝", title: "Too much manual data entry", desc: "Replaced with automated capture, OCR extraction, and system sync workflows that eliminate double-handling." },
        { icon: "🔌", title: "Disconnected systems and siloed data", desc: "Unified with API integrations and real-time data flows so every team works from the same source of truth." },
        { icon: "⏳", title: "Slow approvals and bottlenecks", desc: "Streamlined with automated routing, escalation logic, and mobile-friendly approval workflows." }
      ]}/>
      <FeatureRow items={[
        { icon: "📊", title: "No visibility into pipeline or performance", desc: "Resolved with live dashboards, automated reporting, and KPI tracking across teams and departments." },
        { icon: "😤", title: "Systems teams won't use", desc: "Solved with role-appropriate UX, simplified workflows, and tailored training that matches how each role actually works." },
        { icon: "📈", title: "Scaling beyond spreadsheets", desc: "Migrated to structured platforms built for growth — CRM, Creator apps, and automated processes that don't break at scale." }
      ]}/>

      <Divider/>

      {/* FAQ */}
      <SectionHeader label="Industry FAQ" h2="Questions about industry-specific solutions."/>
      <FAQSection items={[
        { q: "Do you build industry-specific templates or custom solutions?", a: "Custom. Every industry has unique workflows. We start with best practices, then configure precisely to how your business operates. No generic templates." },
        { q: "Can you handle compliance requirements for our sector?", a: "Yes. We've built systems for healthcare (patient data handling), finance (audit trails), construction (site compliance), and government (security requirements). We configure with compliance in mind from day one." },
        { q: "What if our industry isn't listed here?", a: "We've likely worked on something similar. Our methodology works across any sector — the specifics change, but the approach to understanding workflows and building clean systems doesn't." }
      ]}/>

      <CTA onNav={onNav} text="Talk to us about your sector" sub="We'll map out a practical solution for your industry"/>
    </div>
  );
}

function ResultsPage({ page, onNav }) {
  return (
    <div style={{ padding: "160px 64px 80px", maxWidth: 1280, margin: "0 auto" }}>
      <SectionHeader label="Client Results" h2={page.h1} sub={page.content}/>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18, marginTop: 48 }}>
        {(page.cases || []).map((c, i) => (
          <Glass key={i} style={{ padding: 32, borderRadius: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.3)", marginBottom: 16 }}>{c.industry}</div>
            <div style={{ fontSize: 38, fontWeight: 200, color: "#fff", letterSpacing: "-.03em", lineHeight: 1, marginBottom: 6 }}>{c.metric}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,.6)", marginBottom: 16 }}>{c.label}</div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,.42)", lineHeight: 1.65 }}>{c.desc}</p>
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,.07)", fontSize: 11, color: "rgba(255,255,255,.28)" }}>{c.tech}</div>
          </Glass>
        ))}
      </div>

      <div style={{ marginTop: 20, textAlign: "center", padding: 20, borderRadius: 14, background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.06)" }}>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,.4)" }}>Client names withheld by agreement. <span onClick={() => onNav("contact")} style={{ color: "rgba(255,255,255,.65)", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,.2)" }}>Contact us</span> to speak with a reference client in your industry.</p>
      </div>

      <Divider/>

      {/* Delivery approach */}
      <SectionHeader label="Our Delivery Approach" h2="How every project moves from idea to outcome."/>
      <ProcessSteps steps={[
        { title: "Discovery", desc: "Understand your process, data flows, and pain points before recommending anything." },
        { title: "Solution Design", desc: "Architecture, data model, and integration map scoped clearly before build begins." },
        { title: "Build & Configure", desc: "Iterative delivery with regular check-ins, testing, and feedback loops." },
        { title: "Go-Live & Support", desc: "Training, handover, and ongoing support to keep systems running well." }
      ]}/>

      <Divider/>

      {/* Testimonial-style quotes */}
      <SectionHeader label="What Clients Say" h2="Why businesses keep working with us."/>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 32 }}>
        {[
          { quote: "They understood our workflow before touching any configuration. That's rare.", who: "Operations Manager · Logistics company, Sydney" },
          { quote: "The system actually works the way our team works. People use it without being forced.", who: "CEO · Professional services firm, Canberra" },
          { quote: "We went from 3 disconnected tools to one unified platform. Our reporting went from days to minutes.", who: "Finance Director · Manufacturing, Melbourne" },
          { quote: "Post-launch support has been excellent. They treat our system like it's theirs.", who: "IT Manager · Healthcare group, Sydney" }
        ].map((t, i) => (
          <GlassLight key={i} style={{ padding: 28, borderRadius: 18 }}>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,.7)", lineHeight: 1.65, fontStyle: "italic", marginBottom: 14 }}>"{t.quote}"</p>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.35)" }}>{t.who}</div>
          </GlassLight>
        ))}
      </div>

      <CTA onNav={onNav} text="Have a project in mind?" sub="Let's talk about what transformation could look like for you."/>
    </div>
  );
}

function SupportPage({ page, onNav }) {
  return (
    <div style={{ padding: "160px 64px 80px", maxWidth: 1280, margin: "0 auto" }}>
      <SectionHeader label="Ongoing Support" h2={page.h1} sub={page.content}/>

      {/* Tier cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginTop: 48 }}>
        {(page.tiers || []).map((t, i) => (
          <Glass key={i} style={{ padding: 28, display: "flex", flexDirection: "column", border: t.recommended ? "1px solid rgba(255,255,255,.2)" : undefined, borderRadius: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.35)" }}>{t.name}</span>
              {t.recommended && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", background: "#fff", color: "#000", padding: "3px 8px", borderRadius: 999 }}>Recommended</span>}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 6 }}>{t.sub}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)", marginBottom: 20, paddingBottom: 20, borderBottom: "1px solid rgba(255,255,255,.07)" }}>{t.desc}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
              {t.features.map((f, j) => <span key={j} style={{ fontSize: 12, color: "rgba(255,255,255,.6)", display: "flex", gap: 8 }}><span style={{ color: "#22c55e", fontWeight: 700, flexShrink: 0 }}>✓</span>{f}</span>)}
              {(t.missing || []).map((m, j) => <span key={j} style={{ fontSize: 12, color: "rgba(255,255,255,.25)", display: "flex", gap: 8 }}><span style={{ flexShrink: 0 }}>—</span>{m}</span>)}
            </div>
            <button onClick={() => onNav("contact")} style={{ marginTop: 24, padding: "10px 20px", borderRadius: 999, fontSize: 12, fontWeight: 700, border: t.recommended ? "none" : "1.5px solid rgba(255,255,255,.15)", background: t.recommended ? "#fff" : "transparent", color: t.recommended ? "#000" : "#fff", cursor: "pointer", width: "100%", textAlign: "center" }}>Enquire Now</button>
          </Glass>
        ))}
      </div>

      <div style={{ marginTop: 16, textAlign: "center", fontSize: 12, color: "rgba(255,255,255,.3)" }}>All packages include onboarding, a dedicated point of contact, and access to our Australian-based team.</div>

      <Divider/>

      {/* What's included in every plan */}
      <SectionHeader label="Every Plan Includes" h2="Standard inclusions across all support tiers."/>
      <FeatureRow items={[
        { icon: "🐛", title: "Bug Fixes & Issue Resolution", desc: "Any system issues are addressed within your SLA timeframe. We diagnose, fix, and document every resolution." },
        { icon: "👤", title: "User Management", desc: "Adding and removing users, updating permissions, managing roles, and ensuring your team has the right access." },
        { icon: "📞", title: "Dedicated Point of Contact", desc: "You always know who to reach. One person owns your account and understands your system inside out." }
      ]}/>
      <FeatureRow items={[
        { icon: "📝", title: "Change Documentation", desc: "Every change we make is documented. Your team always knows what was updated and why." },
        { icon: "🚀", title: "Onboarding", desc: "New to a support plan? We start with a full system review to understand your current setup before anything else." },
        { icon: "🇦🇺", title: "Australian Business Hours", desc: "Support is available during AEST/AEDT business hours. No overnight delays, no timezone mismatches." }
      ]}/>

      <Divider/>

      {/* FAQ */}
      <SectionHeader label="Support FAQ" h2="Questions about our support packages."/>
      <FAQSection items={[
        { q: "Can I change my support tier later?", a: "Yes. You can upgrade or downgrade at any time with 30 days notice. Most clients start with Business and move to Growth as their systems expand." },
        { q: "What counts as 'support hours'?", a: "Any work we do on your systems — bug fixes, minor configuration changes, user management, troubleshooting, and guidance calls. Major new features or projects are scoped separately." },
        { q: "Do unused hours roll over?", a: "No. Hours are per month and don't accumulate. This keeps pricing fair — you're paying for availability and priority, not banking time." },
        { q: "What's the difference between support and a new project?", a: "Support covers maintenance, fixes, minor changes, and optimisation of existing systems. If you need a new integration, a new app, or a major workflow redesign, that's a project — we'll scope and quote it separately." },
        { q: "Is there a minimum contract period?", a: "No lock-in. Monthly billing, cancel anytime with 30 days notice. We keep clients because the service is good, not because of contracts." }
      ]}/>

      <CTA onNav={onNav} text="Talk to us about support" sub="We'll recommend the right tier for your business"/>
    </div>
  );
}

function ContactPage({ page, onNav }) {
  const [sent, setSent] = useState(false);
  return (
    <div style={{ padding: "160px 64px 80px", maxWidth: 1280, margin: "0 auto" }}>
      <SectionHeader label="Get in Touch" h2={page.h1} sub={page.content}/>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, marginTop: 48 }}>
        <Glass style={{ padding: 32, borderRadius: 20 }}>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,.5)", marginBottom: 24 }}>Send an enquiry — we'll respond within one business day.</p>
          {[["Name *", "text"], ["Email *", "email"], ["Company", "text"]].map(([l, t], i) => (
            <div key={i} style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.4)", marginBottom: 6 }}>{l}</label>
              <input type={t} style={{ width: "100%", padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(255,255,255,.12)", background: "rgba(255,255,255,.05)", color: "#fff", fontSize: 14, fontFamily: "inherit", outline: "none" }}/>
            </div>
          ))}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.4)", marginBottom: 6 }}>Service of Interest</label>
            <select style={{ width: "100%", padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(255,255,255,.12)", background: "rgba(255,255,255,.08)", color: "#fff", fontSize: 14, fontFamily: "inherit", outline: "none" }}>
              <option value="">Select a service…</option>
              {["Business Process Automation", "Zoho Implementation", "Microsoft & Cloud Solutions", "Custom Software Development", "System Integration & APIs", "AI & Document Intelligence", "Excel Macros & Automation", "Not Sure – Need Advice"].map((s, i) => <option key={i} value={s} style={{ background: "#111" }}>{s}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.4)", marginBottom: 6 }}>Message *</label>
            <textarea rows={4} style={{ width: "100%", padding: "12px 16px", borderRadius: 12, border: "1px solid rgba(255,255,255,.12)", background: "rgba(255,255,255,.05)", color: "#fff", fontSize: 14, fontFamily: "inherit", outline: "none", resize: "vertical" }}/>
          </div>
          <button onClick={() => setSent(true)} style={{ width: "100%", padding: "13px", borderRadius: 999, background: sent ? "#22c55e" : "#fff", color: sent ? "#fff" : "#000", fontSize: 14, fontWeight: 700, border: "none", cursor: "pointer" }}>{sent ? "Enquiry Sent ✓" : "Submit Enquiry ↗"}</button>
        </Glass>

        <div>
          <h3 style={{ fontSize: 17, fontWeight: 600, color: "#fff", marginBottom: 20 }}>Company Details</h3>
          {[["🏢", "Company", "Tech Dynamic Pty Ltd"], ["📍", "Sydney", "Level 1/5 George St, North Strathfield NSW 2137"], ["📍", "Canberra", "Level 2/7 Neptune St, Phillip ACT 2606"], ["📍", "Melbourne", "Level 21/567 Collins St, Melbourne VIC 3000"], ["📧", "Email", "projects@techdynamic.com.au"], ["🌐", "Website", "techdynamic.com.au"]].map(([ic, l, v], i) => (
            <GlassLight key={i} style={{ padding: "16px 18px", display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10, borderRadius: 14 }}>
              <span style={{ fontSize: 18 }}>{ic}</span>
              <div><div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.3)", marginBottom: 3 }}>{l}</div><div style={{ fontSize: 13, color: "#fff" }}>{v}</div></div>
            </GlassLight>
          ))}

          <Glass style={{ borderRadius: 16, padding: 24, marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.3)", marginBottom: 14 }}>What happens next?</div>
            {[
              ["1", "We review your enquiry and respond within one business day."],
              ["2", "We schedule a 30-minute discovery call to understand your needs."],
              ["3", "We propose a tailored approach with clear scope, timeline, and cost."]
            ].map(([n, t], i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: i < 2 ? "1px solid rgba(255,255,255,.06)" : "none" }}>
                <div style={{ width: 24, height: 24, borderRadius: 6, background: "rgba(255,255,255,.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{n}</div>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,.5)", lineHeight: 1.5 }}>{t}</p>
              </div>
            ))}
          </Glass>

          <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {["No lock-in contracts", "Australian-based team", "Certified Zoho experts", "Free scoping session"].map((t, i) => (
              <span key={i} style={{ fontSize: 11, color: "rgba(255,255,255,.35)", display: "flex", alignItems: "center", gap: 5 }}><span style={{ color: "#22c55e" }}>✓</span>{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LocationPage({ page, onNav }) {
  const city = page.slug.replace("locations-", "");
  const cityName = city.charAt(0).toUpperCase() + city.slice(1);
  return (
    <div style={{ padding: "160px 64px 80px", maxWidth: 1280, margin: "0 auto" }}>
      <SectionHeader label={`${cityName} Office`} h2={page.h1} sub={page.content}/>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, marginTop: 40 }}>
        <div>
          <GlassLight style={{ padding: 32, borderRadius: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.3)", marginBottom: 8 }}>Office Address</div>
            <div style={{ fontSize: 18, color: "#fff", fontWeight: 500, marginBottom: 20 }}>{page.address}</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.45)", lineHeight: 1.7, marginBottom: 20 }}>Our {cityName} team delivers Zoho implementation, Microsoft 365 setup, business automation, custom software development, and ongoing support to local businesses.</div>
            <button onClick={() => onNav("contact")} style={{ padding: "11px 24px", borderRadius: 999, background: "#fff", color: "#000", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer" }}>Book a Consultation ↗</button>
          </GlassLight>

          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: "#fff", marginBottom: 12 }}>Services available from {cityName}</h4>
            {["Business Process Automation", "Zoho CRM & One Implementation", "Microsoft 365 & Azure", "Custom Software Development", "API Integration & Data Sync", "AI & Document Intelligence", "Excel Macros & Reporting", "Ongoing Support & Optimisation"].map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 6, fontSize: 12, color: "rgba(255,255,255,.45)", padding: "5px 0" }}>
                <span style={{ color: "#22c55e", flexShrink: 0 }}>✓</span>{s}
              </div>
            ))}
          </div>
        </div>

        <div>
          <Glass style={{ padding: 28, borderRadius: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.3)", marginBottom: 16 }}>Contact {cityName}</div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,.6)", marginBottom: 6 }}>Email: projects@techdynamic.com.au</div>
            <div style={{ fontSize: 14, color: "rgba(255,255,255,.6)", marginBottom: 20 }}>Web: techdynamic.com.au</div>
            <div style={{ borderTop: "1px solid rgba(255,255,255,.06)", paddingTop: 16 }}>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,.45)", lineHeight: 1.7 }}>We also serve clients remotely across all of Australia. Whether you're in {cityName} or regional areas, we can scope, build, and support your systems.</div>
            </div>
          </Glass>

          <div style={{ marginTop: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: "#fff", marginBottom: 12 }}>Other offices</h4>
            {[["Sydney", "locations-sydney"], ["Canberra", "locations-canberra"], ["Melbourne", "locations-melbourne"]].filter(([, s]) => s !== page.slug).map(([name, slug], i) => (
              <div key={i} onClick={() => onNav(slug)} style={{ fontSize: 13, color: "rgba(255,255,255,.5)", padding: "6px 0", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,.05)" }}>
                📍 {name} Office →
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTA onNav={onNav}/>
    </div>
  );
}

function SectionHeader({ label, h2, sub }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".18em", textTransform: "uppercase", color: "rgba(255,255,255,.38)", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ width: 18, height: 1, background: "rgba(255,255,255,.25)", display: "inline-block" }}/>{label}
      </div>
      <h2 style={{ fontSize: "clamp(28px,3.5vw,44px)", fontWeight: 600, letterSpacing: "-.03em", lineHeight: 1.15, color: "#fff", marginBottom: 12 }}>{h2}</h2>
      {sub && <p style={{ fontSize: 15, color: "rgba(255,255,255,.45)", lineHeight: 1.7, maxWidth: 500 }}>{sub}</p>}
    </div>
  );
}

// ── ADMIN PANEL ──
function AdminPanel({ pages, setPages }) {
  const [sel, setSel] = useState("home");
  const page = pages[sel] || {};
  const seo = calcSEO(page);
  const allSEO = useMemo(() => Object.entries(pages).map(([k, v]) => ({ slug: k, label: v.navLabel || v.slug, ...calcSEO(v) })), [pages]);
  const avgScore = Math.round(allSEO.reduce((a, b) => a + b.score, 0) / allSEO.length);

  const update = (field, val) => {
    setPages(prev => ({ ...prev, [sel]: { ...prev[sel], [field]: val } }));
  };

  const inputStyle = { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,.12)", background: "rgba(255,255,255,.05)", color: "#fff", fontSize: 13, fontFamily: "inherit", outline: "none" };
  const labelStyle = { display: "block", fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.35)", marginBottom: 5, marginTop: 16 };

  return (
    <div style={{ padding: "130px 48px 80px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-.03em" }}>SEO Admin Panel</h1>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,.4)", marginTop: 4 }}>Manage page content, meta tags, and track SEO scores</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 36, fontWeight: 200, color: scoreColor(avgScore) }}>{avgScore}</div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.3)" }}>Avg Score</div>
          </div>
        </div>
      </div>

      {/* All pages overview */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 8, marginBottom: 32 }}>
        {allSEO.map(p => (
          <div key={p.slug} onClick={() => setSel(p.slug)} style={{
            padding: "14px 16px", borderRadius: 12, cursor: "pointer", transition: "all .15s",
            background: sel === p.slug ? "rgba(255,255,255,.1)" : "rgba(255,255,255,.03)",
            border: sel === p.slug ? "1px solid rgba(255,255,255,.2)" : "1px solid rgba(255,255,255,.06)"
          }}>
            <div style={{ fontSize: 22, fontWeight: 200, color: scoreColor(p.score), marginBottom: 4 }}>{p.score}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.label}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,.3)" }}>/{p.slug}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 24 }}>
        {/* Editor */}
        <Glass style={{ padding: 28 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Editing: /{page.slug}</h3>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,.3)", marginBottom: 8 }}>Changes are saved automatically in this session</p>

          <label style={labelStyle}>Page Title <span style={{ color: "rgba(255,255,255,.2)" }}>({(page.title || "").length}/60 chars)</span></label>
          <input value={page.title || ""} onChange={e => update("title", e.target.value)} style={inputStyle}/>
          {/* Google preview */}
          <div style={{ marginTop: 12, padding: 16, borderRadius: 10, background: "#fff" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: "#888", marginBottom: 6, letterSpacing: ".06em", textTransform: "uppercase" }}>Google Search Preview</div>
            <div style={{ fontSize: 16, color: "#1a0dab", fontWeight: 400, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{page.title || "Page Title"}</div>
            <div style={{ fontSize: 12, color: "#006621", margin: "3px 0" }}>techdynamic.com.au/{page.slug === "home" ? "" : page.slug}</div>
            <div style={{ fontSize: 13, color: "#545454", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{page.metaDesc || "Add a meta description..."}</div>
          </div>

          <label style={labelStyle}>Meta Description <span style={{ color: "rgba(255,255,255,.2)" }}>({(page.metaDesc || "").length}/160 chars)</span></label>
          <textarea value={page.metaDesc || ""} onChange={e => update("metaDesc", e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }}/>

          <label style={labelStyle}>H1 Heading</label>
          <input value={page.h1 || ""} onChange={e => update("h1", e.target.value)} style={inputStyle}/>

          <label style={labelStyle}>Target Keywords <span style={{ color: "rgba(255,255,255,.2)" }}>(comma separated, 3-8)</span></label>
          <input value={page.keywords || ""} onChange={e => update("keywords", e.target.value)} style={inputStyle}/>

          <label style={labelStyle}>Page Content / Description</label>
          <textarea value={page.content || ""} onChange={e => update("content", e.target.value)} rows={5} style={{ ...inputStyle, resize: "vertical" }}/>

          <label style={labelStyle}>Schema Type</label>
          <select value={page.schema || ""} onChange={e => update("schema", e.target.value)} style={{ ...inputStyle, background: "rgba(255,255,255,.08)" }}>
            <option value="">None</option>
            <option value="ProfessionalService">ProfessionalService</option>
            <option value="Service">Service</option>
            <option value="LocalBusiness">LocalBusiness</option>
            <option value="Article">Article</option>
            <option value="ContactPage">ContactPage</option>
            <option value="FAQPage">FAQPage</option>
          </select>

          <label style={labelStyle}>URL Slug</label>
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <span style={{ padding: "10px 12px", background: "rgba(255,255,255,.08)", borderRadius: "10px 0 0 10px", border: "1px solid rgba(255,255,255,.12)", borderRight: "none", fontSize: 13, color: "rgba(255,255,255,.35)" }}>techdynamic.com.au/</span>
            <input value={page.slug || ""} onChange={e => update("slug", e.target.value.toLowerCase().replace(/\s/g, "-"))} style={{ ...inputStyle, borderRadius: "0 10px 10px 0" }}/>
          </div>
        </Glass>

        {/* SEO Scorecard */}
        <div>
          <Glass style={{ padding: 24, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "-.01em" }}>SEO Score</span>
              <span style={{ fontSize: 32, fontWeight: 200, color: scoreColor(seo.score) }}>{seo.score}<span style={{ fontSize: 14, color: "rgba(255,255,255,.3)" }}>/100</span></span>
            </div>
            {/* Score bar */}
            <div style={{ height: 6, background: "rgba(255,255,255,.08)", borderRadius: 999, overflow: "hidden", marginBottom: 20 }}>
              <div style={{ height: "100%", width: seo.score + "%", background: scoreColor(seo.score), borderRadius: 999, transition: "width .4s ease" }}/>
            </div>

            {/* Wins */}
            {seo.wins.length > 0 && <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#22c55e", marginBottom: 8 }}>Passing</div>
              {seo.wins.map((w, i) => <div key={i} style={{ fontSize: 12, color: "rgba(255,255,255,.5)", padding: "5px 0", display: "flex", gap: 6 }}><span style={{ color: "#22c55e", flexShrink: 0 }}>✓</span>{w}</div>)}
            </div>}

            {/* Issues */}
            {seo.issues.length > 0 && <div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "#f59e0b", marginBottom: 8 }}>Issues to Fix</div>
              {seo.issues.map((w, i) => <div key={i} style={{ fontSize: 12, color: "rgba(255,255,255,.5)", padding: "5px 0", display: "flex", gap: 6 }}><span style={{ color: "#f59e0b", flexShrink: 0 }}>!</span>{w}</div>)}
            </div>}
          </Glass>

          {/* Quick tips */}
          <Glass style={{ padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "rgba(255,255,255,.3)", marginBottom: 12 }}>Quick Tips</div>
            {[
              "Keep title under 60 characters",
              "Meta description: 120–160 chars",
              "Target 3–8 keywords per page",
              "Put primary keyword early in title",
              "Each page should target unique keywords",
              "Add 300+ words of content per page",
              "Publish 1 blog post per week"
            ].map((t, i) => <div key={i} style={{ fontSize: 11, color: "rgba(255,255,255,.38)", padding: "4px 0", display: "flex", gap: 6 }}><span style={{ color: "rgba(255,255,255,.2)" }}>•</span>{t}</div>)}
          </Glass>
        </div>
      </div>
    </div>
  );
}

// ── FOOTER ──
function Footer({ onNav }) {
  return (
    <footer style={{ borderTop: "1px solid rgba(255,255,255,.07)", padding: "56px 64px 36px", background: "rgba(0,0,0,.4)", backdropFilter: "blur(12px)" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2.2fr 1fr 1fr 1fr", gap: 48, marginBottom: 44 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <svg width={22} height={22} viewBox="0 0 26 26" fill="none"><rect x={0} y={3} width={9} height={20} rx={2.5} fill="#fff"/><rect x={13} y={3} width={9} height={20} rx={2.5} fill="#fff" opacity={.35}/></svg>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Tech<span style={{ fontWeight: 200, color: "rgba(255,255,255,.5)" }}> Dynamic</span></span>
            </div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,.35)", lineHeight: 1.7, maxWidth: 280 }}>Authorised Zoho Partner delivering automation, CRM, Microsoft 365, and custom software for Australian businesses.</p>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.35)", marginTop: 14 }}>projects@techdynamic.com.au</div>
          </div>
          <div>
            <h4 style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(255,255,255,.28)", marginBottom: 14 }}>Services</h4>
            {["Business Automation", "Zoho Implementation", "Microsoft & Cloud", "Custom Software", "API Integrations", "AI & Document Intel"].map((s, i) =>
              <div key={i} onClick={() => onNav("services")} style={{ fontSize: 13, color: "rgba(255,255,255,.42)", marginBottom: 9, cursor: "pointer" }}>{s}</div>
            )}
          </div>
          <div>
            <h4 style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(255,255,255,.28)", marginBottom: 14 }}>Company</h4>
            {[["Zoho Partner", "zoho"], ["Industries", "industries"], ["Results", "results"], ["Support", "support"], ["Contact", "contact"]].map(([l, s], i) =>
              <div key={i} onClick={() => onNav(s)} style={{ fontSize: 13, color: "rgba(255,255,255,.42)", marginBottom: 9, cursor: "pointer" }}>{l}</div>
            )}
          </div>
          <div>
            <h4 style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(255,255,255,.28)", marginBottom: 14 }}>Locations</h4>
            {[["Sydney, NSW", "locations-sydney"], ["Canberra, ACT", "locations-canberra"], ["Melbourne, VIC", "locations-melbourne"]].map(([l, s], i) =>
              <div key={i} onClick={() => onNav(s)} style={{ fontSize: 13, color: "rgba(255,255,255,.42)", marginBottom: 9, cursor: "pointer" }}>{l}</div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 24, borderTop: "1px solid rgba(255,255,255,.05)", fontSize: 12, color: "rgba(255,255,255,.22)" }}>
          <span>© 2026 Tech Dynamic Pty Ltd. All rights reserved.</span>
          <div style={{ display: "flex", gap: 20 }}>
            <span style={{ cursor: "pointer" }}>Privacy</span>
            <span style={{ cursor: "pointer" }}>Terms</span>
            <span>techdynamic.com.au</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── CHATBOT COMPONENT ──
function Chatbot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [step, setStep] = useState(0);
  const [lead, setLead] = useState({ challenge: "", tools: "", size: "", name: "", email: "" });
  const [showInput, setShowInput] = useState(false);
  const [options, setOptions] = useState([]);
  const [typing, setTyping] = useState(false);
  const [placeholder, setPlaceholder] = useState("Type your answer…");
  const [chatHistory, setChatHistory] = useState([]);

  const addBot = useCallback((text) => {
    setMsgs(prev => [...prev, { role: "bot", text, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
  }, []);

  const addUser = useCallback((text) => {
    setMsgs(prev => [...prev, { role: "user", text, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
  }, []);

  // Start flow when opened
  useEffect(() => {
    if (open && step === 0) {
      const t1 = setTimeout(() => addBot("Hi! I'm the Tech Dynamic AI assistant. I'll ask a few quick questions so the right person from our team can reach out."), 400);
      const t2 = setTimeout(() => { addBot("What's the main challenge you're trying to solve?"); setStep(1); setShowInput(true); setPlaceholder("e.g. We have too many manual processes…"); }, 1000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [open, step, addBot]);

  const recommendService = (challenge, tools) => {
    const c = challenge.toLowerCase(); const t = tools.toLowerCase();
    if (c.includes("invoice") || c.includes("finance")) return "AI & Document Intelligence";
    if (c.includes("crm") || c.includes("sales")) return "Zoho CRM Implementation";
    if (t.includes("zoho")) return "Zoho Implementation & Optimisation";
    if (t.includes("microsoft") || c.includes("sharepoint")) return "Microsoft 365 & Cloud";
    if (c.includes("manual") || c.includes("process") || c.includes("workflow")) return "Business Process Automation";
    if (c.includes("integrat") || c.includes("connect")) return "System Integration & APIs";
    if (c.includes("excel") || c.includes("spreadsheet")) return "Excel Macros & Automation";
    if (c.includes("software") || c.includes("app") || c.includes("portal")) return "Custom Software Development";
    return "Business Process Automation";
  };

  const submitToZoho = async (leadData) => {
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514", max_tokens: 500,
          system: "You are a CRM assistant. Create a lead in Zoho CRM with the data provided. Return the result.",
          messages: [{ role: "user", content: `Create a lead in Zoho CRM with: Name: ${leadData.name}, Email: ${leadData.email}, Company Size: ${leadData.size}, Current Tools: ${leadData.tools}, Challenge: ${leadData.challenge}, Service Interest: ${leadData.service}` }],
          mcp_servers: [{ type: "url", url: "https://claude-zohocrm.zohomcp.com.au/mcp/message", name: "zoho-crm" }]
        })
      });
      return await res.json();
    } catch (e) { console.log("Zoho CRM submit error:", e); return null; }
  };

  const handleStep = useCallback((text) => {
    if (step === 1) {
      setLead(prev => ({ ...prev, challenge: text }));
      setStep(2); setShowInput(false);
      setTimeout(() => {
        addBot("Got it. Which platforms are you currently using?");
        setOptions([
          { label: "🔷 Zoho", val: "Zoho" }, { label: "☁️ Microsoft 365", val: "Microsoft 365" },
          { label: "Both", val: "Zoho & Microsoft 365" }, { label: "Neither / Not sure", val: "Not sure" }
        ]);
      }, 300);
    } else if (step === 2) {
      setLead(prev => ({ ...prev, tools: text }));
      setStep(3); setOptions([]);
      setTimeout(() => {
        addBot("How many people are in your business?");
        setOptions([
          { label: "1–10", val: "1–10" }, { label: "11–50", val: "11–50" },
          { label: "51–200", val: "51–200" }, { label: "200+", val: "200+" }
        ]);
      }, 300);
    } else if (step === 3) {
      setLead(prev => ({ ...prev, size: text }));
      setStep(4); setOptions([]);
      setTimeout(() => { addBot("Almost done! What's your name?"); setShowInput(true); setPlaceholder("Your full name"); }, 300);
    } else if (step === 4) {
      const name = text.trim() || "there";
      setLead(prev => ({ ...prev, name }));
      setStep(5);
      setTimeout(() => { addBot(`Nice to meet you, ${name}! And your work email so we can follow up?`); setShowInput(true); setPlaceholder("you@company.com"); }, 300);
    } else if (step === 5) {
      const email = text.trim();
      const updatedLead = { ...lead, email, name: lead.name || "there" };
      setLead(updatedLead);
      setStep(6); setShowInput(false); setOptions([]);
      const service = recommendService(updatedLead.challenge, updatedLead.tools);
      setTimeout(() => addBot(`Thanks ${updatedLead.name}! Here's what I've captured:`), 400);
      setTimeout(() => {
        addBot(`📋 Your Enquiry Summary\n\nChallenge: ${updatedLead.challenge}\nCurrent tools: ${updatedLead.tools}\nTeam size: ${updatedLead.size}\nRecommended service: ${service}\n\nWe'll reach out to ${email} within one business day. ✅`);
        submitToZoho({ ...updatedLead, service });
      }, 900);
      setTimeout(() => {
        addBot("Feel free to ask me anything about our services, Zoho, or how we work.");
        setStep(7); setShowInput(true); setPlaceholder("Ask me anything…");
        setOptions([
          { label: "How does pricing work?", val: "How does pricing work?" },
          { label: "Tell me about Zoho", val: "Tell me about Zoho implementation" },
          { label: "Your offices?", val: "Where are your offices?" }
        ]);
      }, 1600);
    } else if (step === 7) {
      getAIReply(text);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, lead, addBot]);

  const getAIReply = async (text) => {
    const newHistory = [...chatHistory, { role: "user", content: text }];
    setChatHistory(newHistory);
    setTyping(true);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514", max_tokens: 400,
          system: "You are the AI assistant for Tech Dynamic, an Australian IT company and authorised Zoho Partner in Sydney, Canberra, and Melbourne. Services: Business Process Automation, Zoho Implementation, Microsoft 365, Custom Software, API Integration, AI Automation, Excel Macros. 50+ projects, 100% Australian team. Email: projects@techdynamic.com.au. Keep answers to 2-4 sentences. Never quote prices — invite them to book a free discovery call.",
          messages: newHistory.slice(-8)
        })
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "Please contact us at projects@techdynamic.com.au for more info.";
      setTyping(false);
      addBot(reply);
      setChatHistory(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (e) {
      setTyping(false);
      addBot("For detailed questions please reach out to projects@techdynamic.com.au — we respond within one business day.");
    }
  };

  const send = () => {
    if (!input.trim()) return;
    addUser(input.trim());
    handleStep(input.trim());
    setInput("");
  };

  const pickOption = (val, label) => {
    addUser(label);
    setOptions([]);
    handleStep(val);
  };

  const progress = { 0: 0, 1: 10, 2: 30, 3: 50, 4: 70, 5: 88, 6: 100, 7: 100 }[step] || 0;

  // Styles
  const S = {
    fab: { position: "fixed", bottom: 28, right: 28, zIndex: 9000, width: 54, height: 54, borderRadius: 999, background: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(0,0,0,.4)", transition: "all .25s" },
    win: { position: "fixed", bottom: 96, right: 28, zIndex: 9000, width: 360, maxHeight: 540, borderRadius: 20, background: "rgba(10,10,10,.96)", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,.1)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.5)", transition: "all .25s cubic-bezier(.4,0,.2,1)", transform: open ? "scale(1) translateY(0)" : "scale(.95) translateY(10px)", opacity: open ? 1 : 0, pointerEvents: open ? "all" : "none" },
    header: { padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, borderBottom: "1px solid rgba(255,255,255,.08)" },
    msgs: { flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 10, scrollbarWidth: "thin" },
    botBubble: { background: "rgba(255,255,255,.08)", color: "rgba(255,255,255,.85)", padding: "10px 14px", borderRadius: "14px 14px 14px 4px", fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap" },
    userBubble: { background: "#fff", color: "#000", padding: "10px 14px", borderRadius: "14px 14px 4px 14px", fontSize: 13, lineHeight: 1.55 },
    inputRow: { padding: "12px 14px", borderTop: "1px solid rgba(255,255,255,.07)", display: "flex", gap: 8, alignItems: "center", flexShrink: 0 },
    inp: { flex: 1, background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 999, padding: "9px 16px", fontSize: 13, color: "#fff", fontFamily: "inherit", outline: "none" },
    sendBtn: { width: 34, height: 34, borderRadius: "50%", background: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    qbtn: { padding: "5px 12px", borderRadius: 999, border: "1px solid rgba(255,255,255,.12)", background: "transparent", color: "rgba(255,255,255,.6)", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }
  };

  return (
    <>
      <button style={S.fab} onClick={() => setOpen(!open)}>
        <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      </button>

      <div style={S.win}>
        {/* Header */}
        <div style={S.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width={16} height={16} viewBox="0 0 26 26" fill="none"><rect x={1} y={3} width={9} height={20} rx={2.5} fill="#fff"/><rect x={14} y={3} width={9} height={20} rx={2.5} fill="#fff" opacity={.5}/></svg>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Tech Dynamic</div>
              <div style={{ fontSize: 11, color: "#22c55e", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e" }}/>AI Assistant · Online
              </div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid rgba(255,255,255,.1)", background: "transparent", cursor: "pointer", color: "rgba(255,255,255,.5)", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        {/* Progress */}
        <div style={{ height: 3, background: "rgba(255,255,255,.06)", flexShrink: 0 }}>
          <div style={{ height: "100%", background: "#fff", width: progress + "%", transition: "width .4s ease", borderRadius: "0 2px 2px 0" }}/>
        </div>

        {/* Messages */}
        <div style={S.msgs} ref={el => { if (el) el.scrollTop = el.scrollHeight; }}>
          {msgs.map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "82%" }}>
              <div style={m.role === "user" ? S.userBubble : S.botBubble}>{m.text}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.25)", marginTop: 4, textAlign: m.role === "user" ? "right" : "left", padding: "0 4px" }}>{m.time}</div>
            </div>
          ))}
          {typing && (
            <div style={{ alignSelf: "flex-start", background: "rgba(255,255,255,.08)", borderRadius: "14px 14px 14px 4px", padding: "10px 14px", display: "flex", gap: 4 }}>
              {[0, 1, 2].map(i => <span key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "rgba(255,255,255,.4)", animation: "pulse 1.2s ease infinite", animationDelay: i * 0.2 + "s" }}/>)}
            </div>
          )}
        </div>

        {/* Quick buttons */}
        {options.length > 0 && (
          <div style={{ padding: "0 14px 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {options.map((o, i) => (
              <button key={i} style={S.qbtn} onClick={() => pickOption(o.val, o.label)}>{o.label}</button>
            ))}
          </div>
        )}

        {/* Input */}
        {showInput && (
          <div style={S.inputRow}>
            <input style={S.inp} value={input} onChange={e => setInput(e.target.value)} placeholder={placeholder} onKeyDown={e => e.key === "Enter" && send()}/>
            <button style={S.sendBtn} onClick={send}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><line x1={22} y1={2} x2={11} y2={13}/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
        )}
      </div>

      <style>{`@keyframes pulse{0%,100%{opacity:.4}50%{opacity:1}}`}</style>
    </>
  );
}

// ── MAIN APP ──
export default function App() {
  const [pages, setPages] = useState(DEFAULT_PAGES);
  const [current, setCurrent] = useState("home");
  const [isAdmin, setIsAdmin] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const navigate = useCallback((slug) => {
    setCurrent(slug);
    setIsAdmin(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const page = pages[current] || pages.home;

  useEffect(() => {
    document.title = page.title || "Tech Dynamic";
  }, [page.title]);

  const renderPage = () => {
    if (isAdmin) return <AdminPanel pages={pages} setPages={setPages}/>;
    switch (current) {
      case "home": return <HomePage page={page} onNav={navigate}/>;
      case "services": return <ServicesPage page={page} onNav={navigate}/>;
      case "zoho": return <ZohoPage page={page} onNav={navigate}/>;
      case "industries": return <IndustriesPage page={page} onNav={navigate}/>;
      case "results": return <ResultsPage page={page} onNav={navigate}/>;
      case "support": return <SupportPage page={page} onNav={navigate}/>;
      case "contact": return <ContactPage page={page} onNav={navigate}/>;
      default:
        if (current.startsWith("locations-")) return <LocationPage page={page} onNav={navigate}/>;
        return <HomePage page={pages.home} onNav={navigate}/>;
    }
  };

  return (
    <div style={{ fontFamily: "'Inter',system-ui,sans-serif", background: "transparent", color: "#fff", minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      {/* Video BG with fallback */}
      <video
        autoPlay muted loop playsInline
        onCanPlay={() => setVideoLoaded(true)}
        style={{ position: "fixed", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: -2, opacity: videoLoaded ? .7 : 0, transition: "opacity 1s" }}
      >
        <source src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260403_050628_c4e32401-fab4-4a27-b7a8-6e9291cd5959.mp4" type="video/mp4"/>
      </video>
      {/* Fallback animated gradient BG when video doesn't load */}
      {!videoLoaded && (
        <div style={{
          position: "fixed", inset: 0, zIndex: -2,
          background: "radial-gradient(ellipse 80% 60% at 60% 40%, rgba(255,255,255,.06) 0%, transparent 70%), radial-gradient(ellipse 50% 80% at 20% 80%, rgba(255,255,255,.04) 0%, transparent 60%), #000",
        }}/>
      )}
      <div style={{ position: "fixed", inset: 0, zIndex: -1, background: "linear-gradient(135deg,rgba(0,0,0,.5) 0%,rgba(0,0,0,.4) 100%)", pointerEvents: "none" }}/>
      {/* Grid overlay */}
      <div style={{
        position: "fixed", inset: 0, zIndex: -1, pointerEvents: "none", opacity: .4,
        backgroundImage: "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)",
        backgroundSize: "80px 80px",
        maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)",
        WebkitMaskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)"
      }}/>

      <Nav pages={pages} current={current} onNav={navigate} onAdmin={() => setIsAdmin(!isAdmin)} isAdmin={isAdmin}/>
      {renderPage()}
      {!isAdmin && <Footer onNav={navigate}/>}
      {!isAdmin && <Chatbot/>}
    </div>
  );
}
